/**
 * Basic Article Processor
 * 
 * Processes articles to extract:
 * - Sentiment (positive/negative/neutral)
 * - Keywords and entities
 * - Relevance scoring with enhanced filtering
 * - Crisis indicators
 * - Content type classification
 * 
 * KEYWORD TYPES EXPLAINED:
 * 
 * 1. CRISIS KEYWORDS: Used for determining if an article is crisis-relevant
 *    These should match what your monitoring is searching for
 * 
 * 2. ARTICLE KEYWORDS: Extracted from article content for categorization
 *    These are NOT used for relevance scoring, only for display
 * 
 * 3. MATCHED CRISIS KEYWORDS: Which crisis keywords were found in the article
 *    This is what should be displayed in the UI to show WHY the article matched
 */
class BasicArticleProcessor {
  constructor() {
    this.name = 'Enhanced Article Processor';
    
    // CRISIS KEYWORDS: These determine if content is crisis-relevant
    // These should match your monitoring search terms
    this.crisisKeywords = [
      'emergency', 'crisis', 'incident', 'urgent', 'breaking', 'alert',
      'evacuation', 'fire', 'flood', 'earthquake', 'explosion', 'accident',
      'attack', 'threat', 'breach', 'cyber', 'data', 'hack', 'virus',
      'safety', 'security', 'injury', 'death', 'casualty', 'damage',
      'disaster', 'severe', 'critical', 'immediate', 'warning', 'lockdown',
      'outage', 'disruption', 'closure', 'suspended', 'cancelled', 'delayed'
    ];

    // Business/corporate crisis keywords
    this.businessCrisisKeywords = [
      'lawsuit', 'investigation', 'fraud', 'scandal', 'resignation', 'fired',
      'terminated', 'bankruptcy', 'liquidation', 'recall', 'contamination',
      'violation', 'penalty', 'fine', 'suspended', 'banned', 'probe',
      'whistleblower', 'misconduct', 'harassment', 'discrimination'
    ];

    // Content types to filter out (low relevance for crisis management)
    this.lowRelevanceIndicators = [
      // Entertainment/Media
      'podcast', 'episode', 'series', 'season', 'documentary', 'film', 'movie',
      'album', 'song', 'music', 'concert', 'festival', 'celebrity', 'star',
      'actor', 'actress', 'singer', 'musician', 'artist', 'entertainment',
      'hollywood', 'red carpet', 'premiere', 'awards', 'oscar', 'grammy',
      
      // Celebrity/Personal Life
      'divorce', 'marriage', 'wedding', 'relationship', 'dating', 'romance',
      'breakup', 'split', 'ex-husband', 'ex-wife', 'ex-partner', 'couple',
      'baby', 'pregnancy', 'birth', 'family', 'personal life', 'private',
      'autobiography', 'memoir', 'interview', 'profile', 'lifestyle',
      
      // Sports
      'match', 'game', 'tournament', 'championship', 'league', 'team', 'player',
      'coach', 'sport', 'football', 'cricket', 'tennis', 'basketball', 'rugby',
      'olympics', 'medal', 'victory', 'defeat', 'score', 'goal',
      
      // Lifestyle/Opinion
      'recipe', 'fashion', 'style', 'beauty', 'travel', 'holiday', 'vacation',
      'review', 'opinion', 'column', 'editorial', 'commentary', 'blog',
      'horoscope', 'weather forecast', 'lifestyle', 'wellness',
      
      // Technology/Product reviews (unless crisis-related)
      'review', 'unboxing', 'tutorial', 'how-to', 'tips', 'guide',
      'smartphone', 'app launch', 'update', 'feature',
      
      // Regular business news (unless crisis-related)
      'earnings', 'quarterly', 'dividend', 'stock price', 'ipo', 'acquisition'
    ];

    // Podcast-specific indicators
    this.podcastIndicators = [
      'podcast', 'episode', 'listen now', 'subscribe', 'itunes', 'spotify',
      'apple podcasts', 'google podcasts', 'audio', 'transcript', 'host',
      'guest', 'interview', 'conversation', 'discussion', 'talk show'
    ];

    // High-priority news sources (credible, breaking news focused)
    this.credibleSources = [
      'abc.net.au', 'news.com.au', 'theage.com.au', 'smh.com.au',
      'theaustralian.com.au', 'afr.com', 'sbs.com.au', 'theguardian.com',
      'reuters.com', 'bbc.com', 'cnn.com', 'bloomberg.com', 'wsj.com',
      'ft.com', 'ap.org', 'npr.org'
    ];

    // Breaking news indicators
    this.breakingNewsIndicators = [
      'breaking', 'urgent', 'just in', 'developing', 'live updates',
      'latest', 'now', 'alert', 'bulletin', 'flash', 'immediate'
    ];

    // Positive sentiment keywords
    this.positiveWords = [
      'good', 'great', 'excellent', 'positive', 'success', 'successful',
      'achievement', 'progress', 'improvement', 'better', 'best', 'outstanding',
      'pleased', 'happy', 'satisfied', 'effective', 'efficient', 'strong'
    ];

    // Negative sentiment keywords
    this.negativeWords = [
      'bad', 'terrible', 'awful', 'worst', 'fail', 'failure', 'failed',
      'problem', 'issue', 'concern', 'worried', 'disappointed', 'angry',
      'frustrated', 'damage', 'hurt', 'loss', 'decline', 'crisis', 'emergency'
    ];

    // Celebrity names and indicators (Australian and international)
    this.celebrityIndicators = [
      // Australian celebrities
      'hugh jackman', 'deborra-lee furness', 'nicole kidman', 'russell crowe',
      'cate blanchett', 'chris hemsworth', 'margot robbie', 'kylie minogue',
      'keith urban', 'geoffrey rush', 'joel edgerton', 'rebel wilson',
      'liam hemsworth', 'toni collette', 'eric bana', 'simon baker',
      
      // International celebrities (common in Australian media)
      'brad pitt', 'angelina jolie', 'jennifer aniston', 'george clooney',
      'taylor swift', 'beyonce', 'kim kardashian', 'tom cruise',
      'leonardo dicaprio', 'will smith', 'johnny depp', 'amber heard',
      
      // Celebrity relationship terms
      'hollywood couple', 'a-list', 'red carpet', 'paparazzi', 'tabloid',
      'celebrity', 'star', 'famous', 'icon', 'superstar'
    ];

    console.log('⚙️ Enhanced Article Processor initialized');
  }

  /**
   * Process article through all analysis steps
   */
  async process(article) {
    try {
      let processedArticle = { ...article };

      // First, classify content type and filter relevance
      processedArticle.contentType = this.classifyContentType(article);
      processedArticle.sourceCredibility = this.assessSourceCredibility(article);
      
      // Early filtering - skip low-relevance content
      if (this.shouldFilterOut(processedArticle)) {
        processedArticle.filtered = true;
        processedArticle.filterReason = 'Low relevance content type';
        processedArticle.relevanceScore = 0;
        return processedArticle;
      }

      // Extract keywords
      processedArticle.keywords = this.extractKeywords(article);

      // Analyze sentiment
      processedArticle.sentiment = this.analyzeSentiment(article);

      // Calculate enhanced relevance score and track matched keywords
      const relevanceResult = this.calculateEnhancedRelevanceScore(article);
      processedArticle.relevanceScore = relevanceResult.score;
      processedArticle.matchedCrisisKeywords = relevanceResult.matchedKeywords;
      processedArticle.matchReasons = relevanceResult.reasons;

      // Detect crisis indicators
      processedArticle.crisisIndicators = this.detectCrisisIndicators(article);

      // Extract entities (simple version)
      processedArticle.entities = this.extractEntities(article);

      // Add processing metadata
      processedArticle.processingVersion = '2.0';
      processedArticle.processedBy = this.name;
      processedArticle.filtered = false;

      return processedArticle;

    } catch (error) {
      console.error('❌ Error processing article:', error);
      return article; // Return original if processing fails
    }
  }

  /**
   * Classify content type to determine relevance
   */
  classifyContentType(article) {
    const text = `${article.title} ${article.content || ''}`.toLowerCase();
    const url = (article.url || '').toLowerCase();
    
    // Check for celebrity content first (highest priority for filtering)
    const celebrityScore = this.celebrityIndicators.reduce((score, indicator) => {
      return score + (text.includes(indicator) ? 1 : 0);
    }, 0);
    
    if (celebrityScore >= 1) {
      return 'celebrity';
    }
    
    // Check for podcast content
    const podcastScore = this.podcastIndicators.reduce((score, indicator) => {
      return score + (text.includes(indicator) ? 1 : 0);
    }, 0);
    
    if (podcastScore >= 2 || url.includes('podcast')) {
      return 'podcast';
    }

    // Check for entertainment content (expanded detection)
    const entertainmentIndicators = this.lowRelevanceIndicators.filter(indicator =>
      ['entertainment', 'hollywood', 'celebrity', 'star', 'actor', 'actress', 
       'singer', 'musician', 'artist', 'film', 'movie', 'music', 'concert',
       'divorce', 'marriage', 'wedding', 'relationship', 'dating', 'romance',
       'breakup', 'split', 'ex-husband', 'ex-wife', 'red carpet', 'awards'].includes(indicator)
    );
    
    const entertainmentScore = entertainmentIndicators.reduce((score, indicator) => {
      return score + (text.includes(indicator) ? 1 : 0);
    }, 0);

    if (entertainmentScore >= 1) {
      return 'entertainment';
    }

    // Check for sports content
    const sportsIndicators = this.lowRelevanceIndicators.filter(indicator =>
      ['match', 'game', 'tournament', 'sport', 'football', 'cricket', 'tennis'].includes(indicator)
    );
    const sportsScore = sportsIndicators.reduce((score, indicator) => {
      return score + (text.includes(indicator) ? 1 : 0);
    }, 0);

    if (sportsScore >= 2) {
      return 'sports';
    }

    // Check for breaking news
    const breakingScore = this.breakingNewsIndicators.reduce((score, indicator) => {
      return score + (text.includes(indicator) ? 1 : 0);
    }, 0);

    if (breakingScore >= 1) {
      return 'breaking_news';
    }

    // Check for crisis-related content
    const crisisScore = this.crisisKeywords.reduce((score, keyword) => {
      return score + (text.includes(keyword) ? 1 : 0);
    }, 0);

    if (crisisScore >= 2) {
      return 'crisis_news';
    }

    // Default classification
    return 'general_news';
  }

  /**
   * Assess source credibility
   */
  assessSourceCredibility(article) {
    const source = (article.source || '').toLowerCase();
    const url = (article.url || '').toLowerCase();

    // Check against known credible sources
    for (const credibleSource of this.credibleSources) {
      if (source.includes(credibleSource) || url.includes(credibleSource)) {
        return 'high';
      }
    }

    // Check for common blog/social indicators
    if (url.includes('blog') || url.includes('medium.com') || 
        url.includes('wordpress') || url.includes('facebook.com') ||
        url.includes('twitter.com') || url.includes('instagram.com')) {
      return 'low';
    }

    return 'medium';
  }

  /**
   * Determine if article should be filtered out
   */
  shouldFilterOut(article) {
    // Always filter out celebrity content unless it's genuinely crisis-related
    if (article.contentType === 'celebrity') {
      const hasCrisisKeywords = this.crisisKeywords.some(keyword =>
        (article.title || '').toLowerCase().includes(keyword)
      );
      // Only allow celebrity content if it has strong crisis indicators
      return !hasCrisisKeywords;
    }

    // Always filter out podcasts unless they're about breaking crisis news
    if (article.contentType === 'podcast') {
      const hasCrisisKeywords = this.crisisKeywords.some(keyword =>
        (article.title || '').toLowerCase().includes(keyword)
      );
      return !hasCrisisKeywords;
    }

    // Filter out entertainment and sports unless crisis-related
    if (article.contentType === 'entertainment' || article.contentType === 'sports') {
      const hasCrisisKeywords = this.crisisKeywords.some(keyword =>
        (article.title || '').toLowerCase().includes(keyword)
      );
      return !hasCrisisKeywords;
    }

    // Filter out low credibility sources unless breaking news
    if (article.sourceCredibility === 'low' && article.contentType !== 'breaking_news') {
      return true;
    }

    return false;
  }

  /**
   * Calculate enhanced relevance score with improved filtering
   */
  calculateEnhancedRelevanceScore(article) {
    const text = `${article.title} ${article.content || ''}`.toLowerCase();
    const title = (article.title || '').toLowerCase();
    let score = 0;
    const matchedKeywords = [];
    const reasons = [];

    // Content type scoring
    switch (article.contentType) {
      case 'breaking_news':
        score += 0.4;
        reasons.push('Breaking news');
        break;
      case 'crisis_news':
        score += 0.3;
        reasons.push('Crisis news');
        break;
      case 'general_news':
        score += 0.2;
        reasons.push('General news');
        break;
      case 'celebrity':
        score += 0.01; // Extremely low base score for celebrity content
        reasons.push('Celebrity content');
        break;
      case 'podcast':
      case 'entertainment':
      case 'sports':
        score += 0.05; // Very low base score
        reasons.push('Entertainment or sports content');
        break;
      default:
        score += 0.1;
        reasons.push('Other content');
    }

    // Source credibility boost
    switch (article.sourceCredibility) {
      case 'high':
        score += 0.2;
        reasons.push('High credibility source');
        break;
      case 'medium':
        score += 0.1;
        reasons.push('Medium credibility source');
        break;
      case 'low':
        score += 0.0;
        reasons.push('Low credibility source');
        break;
    }

    // Crisis keyword matching (higher weight for title)
    const titleCrisisMatches = this.crisisKeywords.filter(keyword => 
      title.includes(keyword)
    ).length;
    score += titleCrisisMatches * 0.15;
    if (titleCrisisMatches > 0) {
      matchedKeywords.push(...this.crisisKeywords.filter(keyword => 
        title.includes(keyword)
      ));
      reasons.push(`${titleCrisisMatches} crisis keywords matched in title`);
    }

    const contentCrisisMatches = this.crisisKeywords.filter(keyword => 
      text.includes(keyword) && !title.includes(keyword)
    ).length;
    score += Math.min(0.3, contentCrisisMatches * 0.05);
    if (contentCrisisMatches > 0) {
      matchedKeywords.push(...this.crisisKeywords.filter(keyword => 
        text.includes(keyword) && !title.includes(keyword)
      ));
      reasons.push(`${contentCrisisMatches} crisis keywords matched in content`);
    }

    // Business crisis keywords
    const businessCrisisMatches = this.businessCrisisKeywords.filter(keyword => 
      text.includes(keyword)
    ).length;
    score += Math.min(0.2, businessCrisisMatches * 0.08);
    if (businessCrisisMatches > 0) {
      matchedKeywords.push(...this.businessCrisisKeywords.filter(keyword => 
        text.includes(keyword)
      ));
      reasons.push(`${businessCrisisMatches} business crisis keywords matched`);
    }

    // Breaking news indicators
    const breakingMatches = this.breakingNewsIndicators.filter(indicator => 
      title.includes(indicator)
    ).length;
    score += breakingMatches * 0.1;
    if (breakingMatches > 0) {
      matchedKeywords.push(...this.breakingNewsIndicators.filter(indicator => 
        title.includes(indicator)
      ));
      reasons.push(`${breakingMatches} breaking news indicators matched`);
    }

    // Location relevance (Australian content)
    if (article.location || text.includes('australia') || text.includes('australian')) {
      score += 0.1;
      reasons.push('Australian content');
    }

    // Recency boost (breaking news gets higher priority)
    if (article.publishedAt) {
      const hoursAgo = (new Date() - new Date(article.publishedAt)) / (1000 * 60 * 60);
      if (hoursAgo < 2) {
        score += 0.15; // Very recent
        reasons.push('Very recent article');
      }
      else if (hoursAgo < 6) {
        score += 0.1;
        reasons.push('Recent article');
      }
      else if (hoursAgo < 24) {
        score += 0.05;
        reasons.push('Older article');
      }
    }

    // Penalty for low-relevance indicators
    const lowRelevanceMatches = this.lowRelevanceIndicators.filter(indicator => 
      text.includes(indicator)
    ).length;
    score -= Math.min(0.3, lowRelevanceMatches * 0.05);
    if (lowRelevanceMatches > 0) {
      reasons.push(`${lowRelevanceMatches} low relevance indicators matched`);
    }

    // Normalize to 0-1 range
    const normalizedScore = Math.min(1.0, Math.max(0.0, score));

    return {
      score: normalizedScore,
      matchedKeywords,
      reasons
    };
  }

  /**
   * Extract keywords from article text
   */
  extractKeywords(article) {
    const text = `${article.title} ${article.content || ''}`.toLowerCase();
    const words = text.split(/\s+/).filter(word => word.length > 3);
    
    // Remove common stop words
    const stopWords = new Set([
      'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with',
      'by', 'from', 'up', 'about', 'into', 'through', 'during', 'before',
      'after', 'above', 'below', 'between', 'among', 'this', 'that', 'these',
      'those', 'they', 'them', 'their', 'what', 'which', 'who', 'when',
      'where', 'why', 'how', 'said', 'says', 'will', 'would', 'could',
      'should', 'have', 'has', 'had', 'been', 'being', 'were', 'was'
    ]);

    const filteredWords = words.filter(word => 
      !stopWords.has(word) && 
      !/^\d+$/.test(word) && // Remove pure numbers
      word.length > 3
    );

    // Count word frequency
    const wordCount = {};
    filteredWords.forEach(word => {
      wordCount[word] = (wordCount[word] || 0) + 1;
    });

    // Return top keywords sorted by frequency
    return Object.entries(wordCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([word]) => word);
  }

  /**
   * Analyze article sentiment
   */
  analyzeSentiment(article) {
    const text = `${article.title} ${article.content || ''}`.toLowerCase();
    const words = text.split(/\s+/);

    let positiveScore = 0;
    let negativeScore = 0;

    // Count sentiment words
    words.forEach(word => {
      if (this.positiveWords.includes(word)) {
        positiveScore++;
      } else if (this.negativeWords.includes(word)) {
        negativeScore++;
      }
    });

    // Determine overall sentiment
    if (positiveScore > negativeScore) {
      return {
        label: 'positive',
        score: positiveScore / (positiveScore + negativeScore),
        confidence: this.calculateConfidence(positiveScore, negativeScore)
      };
    } else if (negativeScore > positiveScore) {
      return {
        label: 'negative',
        score: negativeScore / (positiveScore + negativeScore),
        confidence: this.calculateConfidence(positiveScore, negativeScore)
      };
    } else {
      return {
        label: 'neutral',
        score: 0.5,
        confidence: 0.5
      };
    }
  }

  /**
   * Calculate confidence for sentiment analysis
   */
  calculateConfidence(positive, negative) {
    const total = positive + negative;
    if (total === 0) return 0.5;
    
    const difference = Math.abs(positive - negative);
    return Math.min(0.95, 0.5 + (difference / total) * 0.45);
  }

  /**
   * Detect crisis indicators in article
   */
  detectCrisisIndicators(article) {
    const text = `${article.title} ${article.content || ''}`.toLowerCase();
    const indicators = [];

    // Emergency indicators
    const emergencyWords = ['emergency', 'urgent', 'immediate', 'critical', 'breaking'];
    emergencyWords.forEach(word => {
      if (text.includes(word)) {
        indicators.push({
          type: 'emergency',
          keyword: word,
          severity: 'high'
        });
      }
    });

    // Incident types
    const incidentTypes = [
      { keywords: ['fire', 'blaze', 'wildfire'], type: 'fire' },
      { keywords: ['flood', 'flooding', 'storm'], type: 'weather' },
      { keywords: ['cyber', 'hack', 'breach', 'data'], type: 'cyber' },
      { keywords: ['accident', 'crash', 'collision'], type: 'accident' },
      { keywords: ['threat', 'attack', 'terrorism'], type: 'security' }
    ];

    incidentTypes.forEach(({ keywords, type }) => {
      const matches = keywords.filter(keyword => text.includes(keyword));
      if (matches.length > 0) {
        indicators.push({
          type: 'incident_type',
          category: type,
          keywords: matches,
          severity: 'medium'
        });
      }
    });

    // Scale indicators
    const scaleWords = [
      { keywords: ['major', 'massive', 'widespread'], scale: 'large' },
      { keywords: ['minor', 'small', 'localized'], scale: 'small' }
    ];

    scaleWords.forEach(({ keywords, scale }) => {
      const matches = keywords.filter(keyword => text.includes(keyword));
      if (matches.length > 0) {
        indicators.push({
          type: 'scale',
          scale: scale,
          keywords: matches,
          severity: scale === 'large' ? 'high' : 'low'
        });
      }
    });

    return indicators;
  }

  /**
   * Extract entities (organisations, locations, people)
   */
  extractEntities(article) {
    const text = `${article.title} ${article.content || ''}`;
          const entities = {
        organisations: [],
        locations: [],
        people: []
      };

    // Simple pattern matching for organisations (words ending in common org suffixes)
    const orgPatterns = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*(?:\s+(?:Corp|Inc|Ltd|Company|Group|Bank|University|Hospital|School)))\b/g;
    let match;
    while ((match = orgPatterns.exec(text)) !== null) {
      entities.organisations.push(match[1]);
    }

    // Extract locations (capitalized words that might be places)
    const locationPatterns = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\b/g;
    const potentialLocations = [];
    while ((match = locationPatterns.exec(text)) !== null) {
      potentialLocations.push(match[1]);
    }

    // Filter locations using known Australian places
    const australianPlaces = [
      'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Darwin', 'Canberra',
      'NSW', 'Victoria', 'Queensland', 'Western Australia', 'South Australia',
      'Northern Territory', 'ACT', 'Tasmania', 'Australia'
    ];

    entities.locations = potentialLocations.filter(place => 
      australianPlaces.some(known => 
        place.toLowerCase().includes(known.toLowerCase()) ||
        known.toLowerCase().includes(place.toLowerCase())
      )
    );

    return entities;
  }

  /**
   * Get processor status
   */
  getStatus() {
    return {
      name: this.name,
      version: '2.0',
      capabilities: [
        'keyword_extraction',
        'sentiment_analysis', 
        'relevance_scoring',
        'crisis_detection',
        'entity_extraction',
        'content_type_classification',
        'source_credibility_assessment'
      ],
      processingTime: 'real-time'
    };
  }
}

export default BasicArticleProcessor; 